import React, { useState, useEffect, useRef } from "react";

import { useLocation, useNavigate } from "react-router-dom";
// internal imports
import { fetchDevices, pairDevice, deleteDevice } from "../../../services/api";
import colors from "../../../theme";
import SnackbarComp from "../../../components/Snackbar/SnackbarComp";

// external imports
import {
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Divider,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import ClearIcon from "@mui/icons-material/Clear";
import TableDatagrid from "../../../components/TableDatagrid/TableDatagrid";

const ChargingPoint = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [filteredData, setFilteredData] = useState([]);
  const [deviceData, setDeviceData] = useState({ chargePointId: "" });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  
  // const isMobileCameraSupported = () => {
  // const device = navigator.userAgent || navigator.vendor || window.opera;
  //   return /android/i.test(device) || /iPhone|iPad|iPod/i.test(device);
  // };
  const fileInputRef = useRef(null);
  const handleQRPairClick = () => {
    setError("Please open camera app of your phone to scan QR code of Charge Point.");
    setStatusCode(200);
    setOpenSnackbar(true);
    // if (isMobileCameraSupported()) {
    //   fileInputRef.current?.click();
    // } else {
    //   setError("Camera access is only supported on Android and iOS devices.");
    //   setStatusCode(400);
    //   setOpenSnackbar(true);
    // }
  };
  useEffect(() => {
    const { message = "", status = 200, redirect } = location.state || {};
    if (message) {
      setError(message);
      setStatusCode(status);
      setOpenSnackbar(true);
      navigate(location.pathname, { replace: true, state: {} });
    }

    if (redirect) {
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
  }, [location, navigate]);

  const columns = [
    {
      field: "deviceId",
      headerName: "ID",
      flex: 0.5,
      headerClassName: "data-grid-header",
    },
    {
      field: "chargePointId",
      headerName: "Device ID",
      flex: 1.5,
      headerClassName: "data-grid-header",
    },
    {
      field: "actions",
      headerName: "Action",
      headerClassName: "data-grid-header",
      type: "actions",
      flex: 1,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ClearIcon sx={{ color: "red" }} />}
          label="Delete"
          onClick={() => {
            setSelectedId(params.id);
            setOpenConfirmDialog(true);
          }}
        />,
      ],
    },
  ];

  useEffect(() => {
    const handleFetchDevices = async () => {
      setIsCheckingStatus(true);
      try {
        const devices = await fetchDevices();
        setFilteredData(devices);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsCheckingStatus(false);
      }
    };
    handleFetchDevices();
  }, []);

  useEffect(() => {
    setIsSaveDisabled(!deviceData.chargePointId);
  }, [deviceData]);

  const handleApiError = (error) => {
    const message = error.response?.data?.message || error.message;
    const status = error.response?.status || 500;
    setError(message);
    setStatusCode(status);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleChange = (field) => (event) => {
    setDeviceData((prevData) => ({
      ...prevData,
      [field]: event.target.value,
    }));
  };

  const handleAction = async (action, id = null) => {
    try {
      let response;
      setIsLoading(true);
      if (action === "pair") {
        response = await pairDevice(deviceData);
      } else if (action === "delete") {
        response = await deleteDevice(id);
        setOpenConfirmDialog(false);
      }
      setOpenSnackbar(true);
      setStatusCode(response.status);
      setError(response.message);
      const devices = await fetchDevices();
      setFilteredData(devices);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingStatus) {
    return (
      <Box
        component="main"
        sx={{ flex: 1, py: 6, px: 4, bgcolor: colors.mainContentBgColor }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        component="main"
        sx={{ flex: 1, px: 0, bgcolor: colors.mainContentBgColor }}
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "0", md: "1rem" },
            bgcolor: colors.mainContentBgColor,
            m: "0rem 1rem",
          }}
        >
          <TextField
            id="ChargePointId"
            placeholder="Enter Charge Point ID"
            type="text"
            variant="standard"
            fullWidth
            required
            value={deviceData.chargePointId}
            onChange={handleChange("chargePointId")}
            sx={{
              width: { xs: "100%", md: "10rem" },
              // width: "100%",
              marginTop: "16px",
              "& .MuiInput-underline:after": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInput-underline.Mui-focused:after": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.secondary,
              },
            }}
          />

          <Button
            sx={{
              width: { sm: "fit-content" },
              height: "fit-content",
              backgroundColor: colors.secondary,
              color: isSaveDisabled || isLoading ? "white" : colors.text,
              fontSize: "15px",
              fontWeight: "bold",
              padding: "8px 12px",
              marginTop: { xs: "16px", sm: "17px" },
              "&:hover": {
                backgroundColor: colors.btnBackgroundColorOnHover,
              },
            }}
            onClick={() => handleAction("pair")}
            disabled={isSaveDisabled || isLoading}
          >
            <Box
              sx={{
                color: isSaveDisabled || isLoading ? "gray" : colors.text,
              }}
            >
              {/* Pair Device */}
              {isLoading ? <CircularProgress size={10} /> : "Pair Using Charge Point ID"}
            </Box>
          </Button>
          <Divider sx={{
            margin:"5px",
            marginTop: {  sm: "28px" },
            '&::before, &::after': {
              borderColor: 'grey.800',
              },
            }}>
            OR
          </Divider>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                // const imageUrl = URL.createObjectURL(file);
              }
            }}
          />
          <Button
            onClick={handleQRPairClick} 
            sx={{
              width: { sm: "fit-content" },
              height: "fit-content",
              backgroundColor: colors.secondary,
              color: isSaveDisabled || isLoading ? "white" : colors.text,
              fontSize: "15px",
              fontWeight: "bold",
              padding: "8px 12px",
              marginTop: { sm: "17px" },
              "&:hover": {
                backgroundColor: colors.btnBackgroundColorOnHover,
              },
            }}>
            Pair Using QR Code
          </Button>
        </Box>
        <Box m="1.5rem 1rem">
          <Box
            mt="10px"
            sx={{
              height: filteredData.length === 0 ? "25vh" : "auto",
            }}
          >
            <TableDatagrid
              data={filteredData}
              columns={columns}
              fontSize="13px"
            />
          </Box>
        </Box>
      </Box>
      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error}
        statusCode={statusCode}
      />
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.mainContentBgColor,
          },
        }}
      >
        <DialogTitle>Unlink Device?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unlink this device?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            sx={{ color: colors.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAction("delete", selectedId)}
            sx={{
              color: colors.text,
              backgroundColor: colors.secondary,
              "&:hover": {
                backgroundColor: colors.btnBackgroundColorOnHover,
              },
            }}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChargingPoint;
